import PasswordInput from '../components/PasswordInput';
import AuthLayout from '../components/AuthLayout';
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [companyCode, setCompanyCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // 初期表示時に保存された入力情報（企業コード・ログインIDのみ）を読み込む
  // 注: パスワードは平文保存しない（M-05）。過去に保存された password フィールドは無視・除去する。
  React.useEffect(() => {
    const savedCredentials = localStorage.getItem('savedCredentials');
    if (savedCredentials) {
      try {
        const credentials = JSON.parse(savedCredentials);
        setCompanyCode(credentials.companyCode || '');
        setUsername(credentials.username || '');
        setRememberMe(true);

        // 旧形式（平文パスワードを含む）が残っていれば、パスワードを除去して再保存する
        if (credentials.password !== undefined) {
          localStorage.setItem('savedCredentials', JSON.stringify({
            companyCode: credentials.companyCode || '',
            username: credentials.username || '',
          }));
        }
      } catch (error) {
        console.error('保存された入力情報の読み込みに失敗:', error);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setEmailNotVerified(false);
    setResendMessage('');

    try {
      // rememberMe はサーバーへも渡す。
      // 永続 Cookie でリフレッシュトークンを保存するか、
      // セッション Cookie（ブラウザを閉じたら消える）にするかの判断に使う。
      await login({ companyCode, username, password, rememberMe });

      // 入力情報を保存（rememberMe がチェックされている場合）
      // パスワードは保存しない（M-05）。ログイン状態の保持はトークン方式に委ねる。
      if (rememberMe) {
        localStorage.setItem('savedCredentials', JSON.stringify({
          companyCode,
          username
        }));
      } else {
        localStorage.removeItem('savedCredentials');
      }

      // ログイン成功後の遷移先は AuthContext で制御されます
    } catch (error: any) {
      const errorData = error.response?.data;
      setError(errorData?.error || 'ログインに失敗しました');

      // ログイン失敗時は保存された認証情報もクリア
      localStorage.removeItem('savedCredentials');
      setRememberMe(false);

      // メール未認証エラーの場合
      if (errorData?.emailNotVerified) {
        setEmailNotVerified(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    setResendMessage('');

    try {
      await axios.post('/api/auth/resend-verification-email', {
        companyCode,
        username
      });

      setResendMessage('認証メールを再送信しました。メールボックスをご確認ください。');
    } catch (error: any) {
      setResendMessage(error.response?.data?.error || '認証メール再送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="ログイン"
      description="企業コード・ログインID・パスワードでログインします。"
      footer={
        <>
          <div>
            <Link to="/forgot-password" style={{ color: '#2563EB' }}>
              パスワードを忘れた方はこちら
            </Link>
          </div>
          <div style={{ marginTop: '8px' }}>
            事業者アカウントをお持ちでない場合は{' '}
            <Link to="/company-register" style={{ color: '#2563EB', fontWeight: 700 }}>
              事業者登録
            </Link>
          </div>
        </>
      }
    >
      
      {error && (
        <div style={{ color: 'red', marginBottom: '20px', padding: '10px', border: '1px solid red', borderRadius: '4px' }}>
          {error}

          {/* メール未認証の場合、再送信ボタンを表示 */}
          {emailNotVerified && (
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={isLoading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2563EB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {isLoading ? '送信中...' : '認証メールを再送信する'}
              </button>
            </div>
          )}
        </div>
      )}

      {resendMessage && (
        <div style={{
          marginBottom: '20px',
          padding: '10px',
          border: '1px solid #28a745',
          borderRadius: '4px',
          backgroundColor: '#d4edda',
          color: '#155724'
        }}>
          {resendMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="companyCode">企業コード:</label>
          <input
            type="text"
            id="companyCode"
            value={companyCode}
            onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
            required
            maxLength={6}
            placeholder="例: ABC123"
            style={{ width: '100%', padding: '8px', marginTop: '5px', textTransform: 'uppercase' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="username">ログインID:</label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            placeholder="例: yamada"
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="password">パスワード:</label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <span>
              ログイン状態を保持する
              <span style={{ display: 'block', fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                次回から入力なしで打刻できます（共有の端末では外してください）
              </span>
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: '#2563EB',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? 'ログイン中...' : 'ログイン'}
        </button>
      </form>

          </AuthLayout>
  );
};

export default LoginPage;